import type { GetStaticProps } from 'next';
import { useState, useEffect } from 'react';
import axios from 'axios';
import BlogSidebar from '../../components/BlogList/BlogSidebar';
import BlogCard from '../../components/BlogSection/BlogCard';
import FeaturedBlogCard from '../../components/BlogList/FeaturedBlogCard';
import BlogPagination from '../../components/BlogList/BlogPagination';
import { Typography } from '../../components/Typography';
import styles from './news.module.scss';
import PageWrapper from '../../components/PageWrapper';
import { formatBlogImageUrl } from '../../utils';
import SEO from '../../components/SEO';

interface BlogListPageProps {
  initialPosts: any[];
  categories: any[];
  totalPosts: number;
  aboutData: any[];
}

// Helper function to format date as "18 Jan 2025"
const formatDate = (dateString: string | Date) => {
  const date = new Date(dateString);
  const day = date.getDate();
  const monthNames = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];
  const month = monthNames[date.getMonth()];
  const year = date.getFullYear();
  return `${day} ${month} ${year}`;
};

const BlogListPage = ({ initialPosts, categories, totalPosts, aboutData }: BlogListPageProps) => {
  const [posts, setPosts] = useState(initialPosts);
  const [filteredPosts, setFilteredPosts] = useState(initialPosts);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [isLoading, setIsLoading] = useState(false);

  const postsPerPage = 12;

  // Filter posts based on search and category
  useEffect(() => {
    let filtered = posts;

    // Filter by category
    if (selectedCategory !== 'all') {
      filtered = filtered.filter((post) =>
        post.categories?.some((cat: any) => cat.slug === selectedCategory)
      );
    }

    // Filter by search query
    if (searchQuery) {
      filtered = filtered.filter(
        (post) =>
          post.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          post.summary?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    setFilteredPosts(filtered);
    setCurrentPage(1); // Reset to first page when filtering
  }, [searchQuery, selectedCategory, posts]);

  // Calculate pagination
  const totalPages = Math.ceil(filteredPosts.length / postsPerPage);
  const startIndex = (currentPage - 1) * postsPerPage;
  const endIndex = startIndex + postsPerPage;
  const currentPosts = filteredPosts.slice(startIndex, endIndex);

  // Format blog posts for BlogCard
  const formatBlogPosts = (apiPosts: any[]) => {
    return apiPosts.map((post) => ({
      id: post._id,
      image: formatBlogImageUrl(post.coverImage?.url),
      category: post.categories?.[0]?.name || 'General',
      readTime: post.readingTime ? `${post.readingTime} min read` : '5 min read',
      title: post.title,
      description: post.summary || 'Read more about this blog post.',
      author: {
        name: 'EPEN Team',
        avatar: '/chat/chat-admin-avatar.png',
        date: post.publishedAt
          ? formatDate(post.publishedAt)
          : formatDate(new Date().toISOString()),
      },
      slug: post.slug,
      body: post.body,
    }));
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const formattedPosts = formatBlogPosts(currentPosts);

  return (
    <>
      <SEO
        title="Blog"
        description="Read the latest industry news, interviews, and insights from EPEN Ltd. Stay updated on video plugin development, software solutions, and technology trends. Explore now!"
        keywords="blog, news, video plugin, software development, epen"
      />
      <PageWrapper
        dateText="Our blog"
        title="Blogs"
        titleText="The latest writings from our team"
        introText="The latest industry news, interviews, technologies, and resources."
        aboutData={aboutData}
        contentContainerClass={styles.contentContainer}
      >
        {/* Main Content */}
        <div className={styles.mainContent}>
          {/* Sidebar */}
          <div className={styles.sidebar}>
            <BlogSidebar
              categories={categories}
              selectedCategory={selectedCategory}
              onCategoryChange={setSelectedCategory}
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
            />
          </div>

          {/* Blog Posts Grid */}
          <div className={styles.postsGrid}>
            {formattedPosts.length > 0 ? (
              <>
                {/* Featured Post (First post in desktop only) */}
                {formattedPosts.length > 0 && (
                  <div className={styles.featuredSection}>
                    <FeaturedBlogCard post={formattedPosts[0]} noBorderRadius={true} />
                  </div>
                )}

                {/* Regular Grid */}
                <div className={styles.grid}>
                  {formattedPosts.map((post) => (
                    <BlogCard key={post.id} post={post} noBorderRadius={true} />
                  ))}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <BlogPagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={handlePageChange}
                  />
                )}
              </>
            ) : (
              <div className={styles.emptyState}>
                <Typography variant="text-xl" color="grayDark-400">
                  No blog posts found
                </Typography>
              </div>
            )}
          </div>
        </div>
      </PageWrapper>
    </>
  );
};

export const getStaticProps: GetStaticProps = async () => {
  try {
    // Fetch blog posts
    const blogResponse = await axios.get(
      `${process.env.NEXT_PUBLIC_REACT_APP_BASE_URL1}/blog-posts?status=published&limit=100&sort=-publishedAt`
    );

    // Fetch categories
    const categoriesResponse = await axios.get(
      `${process.env.NEXT_PUBLIC_REACT_APP_BASE_URL1}/blog-categories`
    );

    // Fetch about data
    const aboutDataResponse = await axios.get(
      `${process.env.NEXT_PUBLIC_REACT_APP_BASE_URL1}/about-us`
    );

    const posts = blogResponse.data.data || [];
    const allCategories = categoriesResponse.data.data || [];

    // Filter categories to only show those with at least one post
    const activeCategorySlugs = new Set<string>();
    posts.forEach((post: any) => {
      post.categories?.forEach((cat: any) => {
        if (cat.slug) {
          activeCategorySlugs.add(cat.slug);
        }
      });
    });

    const activeCategories = allCategories.filter((category: any) =>
      activeCategorySlugs.has(category.slug)
    );

    return {
      props: {
        initialPosts: posts,
        categories: activeCategories,
        totalPosts: blogResponse.data.total || 0,
        aboutData: aboutDataResponse.data.data || [],
      },
      revalidate: 60,
    };
  } catch (error) {
    console.error('Error fetching blog data:', error);
    return {
      props: {
        initialPosts: [],
        categories: [],
        totalPosts: 0,
        aboutData: [],
      },
    };
  }
};

export default BlogListPage;
